import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Row, Col } from "react-bootstrap";

import {
  setShowFeatureDetailsKey,
  setWalkthrough,
  setWelcomeWalkthrough,
  toast,
} from "Layout/slice/index";

import { useNavigation } from "utils/navigate";
import { parseErrorMessage } from "utils/index";
import { Loader } from "components/Loader/index";
import { Button } from "components/Button/index";
import { getSuggestedStrategies } from "services/index";
import { StrategyCard } from "shared/StrategyCard/index";
import { SnaptradeModal } from "shared/Modal/SnaptradeModal";
import { SuggestedStrategyModalContainer } from "./SuggestedStrategyModal.styles";
import { useConnectedAccounts } from "features/Dashboard/hook/useConnectedAccounts";

export const SuggestedStrategyModal = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const { navigateToV2Marketplace, navigateToV2MarketplaceDetail } =
    useNavigation();
  const { refetch: refetchConnectedAccounts } = useConnectedAccounts();

  const [isLoading, setIsLoading] = useState(false);
  const [suggestedStrategies, setSuggestedStrategies] = useState([]);
  const [selectedStrategyData, setSelectedStrategyData] = useState(null);
  const [showSnaptradeModal, setShowSnaptradeModal] = useState(false);

  useEffect(() => {
    getSuggestedStrategyData();
  }, []);

  const getSuggestedStrategyData = async () => {
    try {
      setIsLoading(true);
      const data = await getSuggestedStrategies();
      setSuggestedStrategies(data);
      dispatch(setShowFeatureDetailsKey("welcome"));
      dispatch(setWalkthrough({ run: true, stepIndex: 0 }));

      if (data.length > 0) {
        setSelectedStrategyData(data[0].strategy);
      }
    } catch (error) {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleStrategyClick = (strategy) => {
    setSelectedStrategyData(strategy);
  };

  const refetchPortfolio = () => {
    refetchConnectedAccounts();
  };

  const handleExploreNavigation = () => {
    onHide();
    navigateToV2Marketplace();
  };

  const navigateToDetail = (id) => {
    navigateToV2MarketplaceDetail(id);
    dispatch(setWelcomeWalkthrough(true));
    dispatch(setShowFeatureDetailsKey("welcomeInvest"));
    dispatch(setWalkthrough({ run: true, stepIndex: 0 }));
    onHide();
  };

  const toggleSnaptradeModal = () => {
    setShowSnaptradeModal(!showSnaptradeModal);
  };

  return (
    <SuggestedStrategyModalContainer show={show} onHide={onHide} size={"xl"}>
      <div className="heading">
        <h2>Our top-performing strategy for beginners</h2>
      </div>
      <div className="body">
        {isLoading ? (
          <Loader />
        ) : (
          <div>
            <span
              className="choose-title"
              onClick={() => navigateToDetail(selectedStrategyData.strategy_id)}
            >
              Choose strategy
            </span>
            <div className="icons-wrapper">
              {suggestedStrategies.map((suggested) => (
                <div
                  key={suggested.icon}
                  onClick={() => handleStrategyClick(suggested.strategy)}
                  className={classNames("icons", {
                    active:
                      selectedStrategyData?.strategy_id ===
                      suggested.strategy.strategy_id,
                  })}
                >
                  <img
                    src={suggested.icon}
                    alt={suggested.strategy.name}
                    className="strategy-icon"
                  />
                </div>
              ))}
            </div>

            {selectedStrategyData && (
              <Row>
                <Col md={4} className="strategy-card-col ">
                  <div className="wt-suggested-strategy">
                    <StrategyCard
                      data={selectedStrategyData}
                      type={"suggested"}
                      handleCardClick={() =>
                        navigateToDetail(selectedStrategyData.strategy_id)
                      }
                    />
                  </div>
                </Col>
                <Col md={8} className="description-col">
                  <div className="description-wrapper">
                    <h2 className="title">Strategy Description</h2>
                    <span>{selectedStrategyData.description}</span>
                  </div>
                </Col>
              </Row>
            )}

            <div className="button-wrapper">
              <Button
                className="button-outlined"
                onClick={handleExploreNavigation}
              >
                I am expert! Explore platform
              </Button>
              <Button onClick={toggleSnaptradeModal}>
                Connect your broker account
              </Button>
            </div>
          </div>
        )}
      </div>

      {showSnaptradeModal && (
        <SnaptradeModal
          show={showSnaptradeModal}
          onHide={toggleSnaptradeModal}
          onSuccess={refetchPortfolio}
        />
      )}
    </SuggestedStrategyModalContainer>
  );
};

SuggestedStrategyModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
};

SuggestedStrategyModal.displayName = "SuggestedStrategyModal";
